import React, { useEffect, useState } from 'react';
import { openBesideChat } from 'shared/components/records/recordsSurface';
import { ExternalLink, Power } from 'lucide-react';
import { useTeam } from 'shared/context/teamContext';
import { getLineage, patchLineage } from 'features/evals3/api/evals3Api';
import { StatusDot, TargetPill } from 'features/evals3/components/EvalsTable';
import ProposalGraph from 'features/evals3/editor/ProposalGraph';
import { EntityLink } from 'shared/components/chat/ChatRichBlocks';

/**
 * An eval Ask created, in the chat (the `eval` answer block).
 *
 * Each eval as the editor shows it — its steps, what it scores, Simple or
 * Triage — with what it would have decided on the recent traces Ask read
 * before creating it, and the examples each way to click into. It is created
 * SWITCHED OFF: Enable here is the approval, and until then nothing scores
 * live traffic. The card reads the eval's state on mount, so a reopened
 * thread shows whether it was since enabled, renamed or deleted.
 */

const LEVEL = { run: 'run', span: 'span', conversation: 'conversation' };

function Examples({ label, items, tone }) {
  if (!items || !items.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
      <span className={tone === 'fail' ? 'text-danger' : 'text-success'}>{label}</span>
      {items.map((x) => {
        const kind = x.span_id ? 'span' : x.run_id ? 'run' : null;
        const id = x.span_id || x.run_id || x.conversation_id;
        const text = kind ? `${kind} ${String(id).slice(0, 8)}` : `conversation ${id}`;
        return (
          <span key={`${kind || 'c'}:${id}`} className="rounded bg-[color:var(--surface-sunken)] px-1.5 py-0.5 font-mono text-[11px]">
            {kind ? <EntityLink kind={kind} id={String(id)}>{text}</EntityLink> : text}
          </span>
        );
      })}
    </div>
  );
}

function OneEval({ e, teamId, teamSlug }) {
  const [live, setLive] = useState({ enabled: Boolean(e.enabled), name: e.name, gone: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let off = false;
    if (!teamId || !e.lineage_id) return undefined;
    getLineage(teamId, e.lineage_id)
      .then((d) => { if (!off && d?.lineage) setLive({ enabled: Boolean(d.lineage.enabled), name: d.lineage.name, gone: false }); })
      .catch((err) => { if (!off && err?.response?.status === 404) setLive((l) => ({ ...l, gone: true })); });
    return () => { off = true; };
  }, [teamId, e.lineage_id]);

  const toggle = async () => {
    setBusy(true); setError(null);
    try {
      const l = await patchLineage(teamId, e.lineage_id, { enabled: !live.enabled });
      setLive((s) => ({ ...s, enabled: Boolean(l?.enabled ?? !s.enabled) }));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not change it');
    } finally { setBusy(false); }
  };
  const open = (mode) => {
    const qs = mode === 'edit' ? `eval=${encodeURIComponent(e.lineage_id)}&mode=edit` : `eval=${encodeURIComponent(e.lineage_id)}`;
    openBesideChat(`/${encodeURIComponent(teamSlug)}/agent-analytics/evals?${qs}`);
  };

  const s = e.sample || {};
  const level = LEVEL[e.target] || 'trace';
  return (
    <div data-testid="eval-created" className="rounded-lg border border-[color:var(--border-default)] bg-surface p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">New eval</div>
          <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-2">
            <StatusDot enabled={live.enabled} />
            <span className="truncate text-[14px] font-semibold text-[color:var(--text-primary)]">{live.name}</span>
            <TargetPill target={e.target} />
            <span className="rounded bg-[color:var(--surface-sunken)] px-1.5 py-0.5 text-[10px] font-medium text-[color:var(--text-secondary)]">{e.mode === 'simple' ? 'Simple' : 'Triage'}</span>
            {(e.agent || e.span_name) && (
              <span className="text-[12px] text-[color:var(--text-muted)]">
                {e.agent ? `on ${e.agent}` : ''}{e.agent && e.span_name ? ' · ' : ''}{e.span_name ? <><span className="font-mono">{e.span_name}</span> spans only</> : null}
              </span>
            )}
          </div>
          {(e.checks || e.description) && <p className="mt-1.5 text-[13px] leading-relaxed text-[color:var(--text-secondary)]">{e.checks || e.description}</p>}
        </div>
      </div>

      {e.graph && (
        <div className="mt-3">
          <ProposalGraph base={e.graph} proposed={e.graph} target={null} height={e.mode === 'simple' ? 170 : Math.min(420, 180 + 30 * Object.keys(e.graph.nodes || {}).length)} legend={false} />
        </div>
      )}

      {s.traces > 0 ? (
        <div className="mt-3 grid gap-1.5 rounded-md bg-[color:var(--surface-sunken)] px-3 py-2">
          <div className="text-[12px] text-[color:var(--text-primary)]" data-testid="eval-sample">
            {s.not_judged > 0
              ? <>Judged {s.traces - s.not_judged} of {s.traces} recent {level}s:{' '}</>
              : <>Tried on {s.traces} recent {level}{s.traces === 1 ? '' : 's'}:{' '}</>}
            <span className="font-semibold text-danger">{s.would_fail} would fail</span>
            {' · '}<span className="font-semibold text-success">{s.would_pass} would pass</span>
            {s.needs_a_person > 0 && <>{' · '}<span className="text-warning">{s.needs_a_person} would wait for a person</span></>}
            {s.errored > 0 && <>{' · '}<span className="text-[color:var(--text-muted)]">{s.errored} errored</span></>}
          </div>
          {Array.isArray(s.why_errored) && s.why_errored.length > 0 && (
            <div className="text-[12px] text-[color:var(--text-muted)]">
              Errored: {s.why_errored.map((x) => `${readableWhy(x.why)}${x.n > 1 ? ` (${x.n})` : ''}`).join('; ')}
            </div>
          )}
          <Examples label="Would fail:" items={s.fail_examples} tone="fail" />
          <Examples label="Would pass:" items={s.pass_examples} tone="pass" />
        </div>
      ) : (
        <div className="mt-3 text-[12px] text-[color:var(--text-muted)]">No recent {level}s to try it on, so it was built from the rule as stated.</div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {live.gone ? (
          <span className="text-[12px] text-[color:var(--text-muted)]">This eval has since been deleted.</span>
        ) : (
          <>
            <button type="button" onClick={toggle} disabled={busy || !teamId}
              className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-[12px] font-medium transition-colors disabled:opacity-60 ${live.enabled ? 'border border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-surface-hover' : 'bg-brand text-ink-onBrand hover:bg-brand-hover'}`}>
              <Power size={13} aria-hidden /> {live.enabled ? 'Turn off' : 'Enable'}
            </button>
            <button type="button" onClick={() => open('edit')}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] px-3 text-[12px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover">
              Open in editor <ExternalLink size={12} aria-hidden />
            </button>
            <span className="text-[12px] text-[color:var(--text-muted)]">
              {live.enabled ? 'On: scoring new traffic.' : 'Off: nothing is scored until you enable it.'}
            </span>
          </>
        )}
        {error && <span className="text-[12px] text-danger" role="alert">{error}</span>}
      </div>
    </div>
  );
}

/** Why a trial result errored, in the reader's words; the engine's own text otherwise. */
export function readableWhy(why) {
  const t = String(why || '');
  if (/no integration_id|default_judge_model|no model (is )?connected/i.test(t)) {
    return 'its judge step has no model to run on. Connect one in Settings → Integrations, or ask Lucid to rebuild it without a judge';
  }
  return t;
}

export default function EvalCreatedCard({ block }) {
  const { selectedTeam } = useTeam();
  const teamId = selectedTeam?.id ?? selectedTeam?._id ?? null;
  const teamSlug = selectedTeam?.slug || selectedTeam?.name || (teamId != null ? String(teamId) : '');
  const created = Array.isArray(block?.created) ? block.created : [];
  if (!created.length) return null;
  return (
    <div className="trodo-answer-block my-3 grid gap-3">
      {created.map((e) => <OneEval key={e.lineage_id} e={e} teamId={teamId} teamSlug={teamSlug} />)}
    </div>
  );
}
